import { Info, Lightbulb, AlertTriangle, Bookmark } from 'lucide-react';
import type { ReactNode } from 'react';

type CalloutType = 'info' | 'tip' | 'warning' | 'takeaway';

interface CalloutProps {
  type?: string;
  children?: ReactNode;
}

const STYLES: Record<CalloutType, { container: string; icon: typeof Info; iconClass: string; label: string }> = {
  info: {
    container: 'bg-[#2D7E32]/5 border-l-4 border-[#2D7E32]',
    icon: Info,
    iconClass: 'text-[#2D7E32]',
    label: 'Note',
  },
  tip: {
    container: 'bg-[#D5B13A]/8 border-l-4 border-[#D5B13A]',
    icon: Lightbulb,
    iconClass: 'text-[#D5B13A]',
    label: 'Tip',
  },
  warning: {
    container: 'bg-amber-50 border-l-4 border-amber-500',
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
    label: 'Heads up',
  },
  takeaway: {
    container: 'bg-[#2E4A3A]/5 border-l-4 border-[#2E4A3A]',
    icon: Bookmark,
    iconClass: 'text-[#2E4A3A]',
    label: 'Takeaway',
  },
};

function resolveType(input?: string): CalloutType {
  if (input === 'tip' || input === 'warning' || input === 'takeaway' || input === 'info') {
    return input;
  }
  if (input === 'caution') return 'warning';
  return 'info';
}

export function Callout({ type, children }: CalloutProps) {
  const variant = STYLES[resolveType(type)];
  const Icon = variant.icon;
  return (
    <div
      className={`my-6 rounded-r-xl rounded-l-md ${variant.container} px-5 py-4 flex gap-3 not-prose`}
      role="note"
      aria-label={variant.label}
    >
      <Icon className={`h-5 w-5 mt-1 flex-shrink-0 ${variant.iconClass}`} aria-hidden="true" />
      <div className="flex-1 text-[#2E4A3A] font-body leading-relaxed [&>p]:my-2 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">
        {children}
      </div>
    </div>
  );
}
